import type { ProjectPersonRow, ProjectRow } from "@/lib/profitability-api";

/**
 * What one month of a project looks like once you click it.
 *
 * Derived here rather than in the page so the two figures that are easy to get
 * wrong -- the running total, and what is left of the budget -- have one
 * definition and a test on it.
 */
export interface MonthDrillIn {
  row: ProjectRow;
  /**
   * Hours from the first month of the SELECTED RANGE through this one.
   *
   * Range-scoped, not project-to-date, because the page only holds the range.
   * That is the same number BudgetBurn plots, which is the point: the chart and
   * the modal opened from it must not disagree about the month clicked.
   */
  cumulativeHours: number;
  /**
   * The budget less the hours burned in the range, or null when no budget is on
   * record.
   *
   * A FLOOR on what has been used, never a verdict: the budget covers the whole
   * project and `cumulativeHours` covers the range, so on a project that ran
   * before the range began this reads high. `variance_pct` on the row is the
   * project-to-date answer, and the UI has to show both rather than let this
   * one pass for it.
   */
  remainingHours: number | null;
}

/**
 * The month `periodId` names, with its running total.
 *
 * `chronological` must be oldest-first -- sorted on period_start, never on the
 * period NAME, which is alphabetical ("April 2024" before "December 2023"). The
 * running total is silently wrong if it is not, which is why this takes the
 * sorted list rather than sorting a copy and hoping the caller agreed.
 *
 * Null when the id is not in the list: a month focused under one range may not
 * exist in the next, and an empty modal is worse than none.
 */
export function monthDrillIn(
  chronological: readonly ProjectRow[],
  periodId: string | null,
  budgetHours: number | null,
): MonthDrillIn | null {
  if (!periodId) return null;
  const index = chronological.findIndex((row) => row.period_id === periodId);
  if (index === -1) return null;
  let cumulativeHours = 0;
  for (let i = 0; i <= index; i += 1) cumulativeHours += chronological[i]?.hours ?? 0;
  const row = chronological[index] as ProjectRow;
  return {
    row,
    cumulativeHours,
    remainingHours: budgetHours === null ? null : budgetHours - cumulativeHours,
  };
}

/**
 * The person-months that belong to one period row.
 *
 * Person rows carry no period_id -- they come from people_on_demand, which
 * keys on the close month itself -- so the join is on the date where both
 * sides have one and on the display name only as a fallback. Matching on the
 * name first would be a join on a string that a locale change could rewrite.
 */
export function peopleInPeriod(
  people: readonly ProjectPersonRow[],
  row: ProjectRow,
): ProjectPersonRow[] {
  return people.filter((person) =>
    person.period_start && row.period_start
      ? person.period_start === row.period_start
      : person.period === row.period,
  );
}
